import CheckCircleIcon from '@material-design-icons/svg/filled/check_circle.svg?react';
import RefreshIcon from '@material-design-icons/svg/filled/refresh.svg?react';
import SendIcon from '@material-design-icons/svg/filled/send.svg?react';
import { useWallet } from '@solana/wallet-adapter-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useTRPC } from '../../hooks/useTRPC';
import {
  createAttestationPayload,
  getAttestationErrorMessage,
} from '../../utils/attestation';
import { getErrorMessage } from '../../utils/errorHandling';
import { captureAndReportError } from '../../utils/errorReporting';
import { Button } from '../ui/button';

interface ResubmitHubspotButtonProps {
  releaseId: string;
  dappName: string;
  whatsNew?: string;
  isProcessed: boolean;
  hasNft: boolean;
  hubspotTicketId?: string | null;
  isCollectionVerified?: boolean;
  submissionStatus?: string;
  onSuccess?: () => void;
}

export function ResubmitHubspotButton({
  releaseId,
  dappName: _dappName,
  whatsNew = '',
  isProcessed,
  hasNft,
  hubspotTicketId,
  isCollectionVerified,
  submissionStatus,
  onSuccess,
}: ResubmitHubspotButtonProps) {
  const trpc = useTRPC();
  const wallet = useWallet();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [shouldRemove, setShouldRemove] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Only show button if:
  // 1. Has NFT minted (release was successfully processed)
  // 2. No HubSpot ticket ID (submission failed or never attempted)
  // 3. Collection is verified (required before submission)
  // 4. Not already submitted (prevents duplicate tickets from failed ticket search)
  const shouldShow =
    isProcessed &&
    hasNft &&
    !hubspotTicketId &&
    isCollectionVerified === true &&
    submissionStatus === 'NotSubmitted';

  // Track visibility with local state to prevent instant disappearance on parent rerender
  useEffect(() => {
    if (shouldShow && !isVisible) {
      setIsVisible(true);
    }
  }, [shouldShow, isVisible]);

  const handleResubmit = async () => {
    if (!wallet.connected || !wallet.publicKey || !wallet.signMessage) {
      toast.error('Please connect your wallet to resubmit');
      return;
    }

    let currentStep: 'attestation' | 'submit' = 'attestation';
    setIsSubmitting(true);

    try {
      toast.info('Generating attestation...', { duration: 3000 });
      const attestationResult = await createAttestationPayload(trpc, wallet);

      toast.info('Resubmitting to store...', { duration: 3000 });
      currentStep = 'submit';

      const submitResult = await trpc.release.submitToStore.mutate({
        releaseId,
        whatsNew,
        criticalUpdate: false,
        attestation: {
          payload: attestationResult.attestationPayload,
          requestUniqueId: attestationResult.requestUniqueId,
        },
        isResubmission: true,
      });

      if (submitResult._tag === 'Right' && submitResult.right.success) {
        setIsSuccess(true);

        // Wait 2 seconds to show success state, then trigger exit animation
        setTimeout(() => {
          setShouldRemove(true);

          // Wait for exit animation to complete (300ms) before refetching data
          setTimeout(() => {
            onSuccess?.();
          }, 300);
        }, 2000);

        toast.success(submitResult.right.message, {
          description: submitResult.right.hubspotTicketId
            ? `Ticket ID: ${submitResult.right.hubspotTicketId}`
            : undefined,
          duration: 8000,
        });
      } else {
        toast.error('Failed to resubmit release to store');
      }
    } catch (error) {
      captureAndReportError('Failed to resubmit:', error);
      const errorMessage = getErrorMessage(error);
      const attestationErrorMessage = getAttestationErrorMessage(error);

      if (currentStep === 'attestation') {
        toast.error('Failed to generate attestation', {
          description: attestationErrorMessage,
        });
      } else if (errorMessage.includes('already been submitted')) {
        toast.error('Release has already been submitted', {
          description: 'Check the submission status above',
        });
      } else {
        toast.error('Failed to resubmit release to store', {
          description: errorMessage,
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <AnimatePresence>
      {!shouldRemove && (
        <motion.div
          key="resubmit-button"
          exit={{
            opacity: 0,
            height: 0,
            marginBottom: 0,
            transition: {
              duration: 0.3,
              ease: [0.4, 0, 0.2, 1],
            },
          }}
          className="overflow-hidden"
        >
          <div className="bg-amber-950/20 rounded-lg p-4 mb-6 border border-amber-800/30">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <RefreshIcon className="size-5 fill-amber-500 flex-shrink-0" />
                <div>
                  <h3 className="text-base font-medium text-amber-300">
                    Resubmit Ticket
                  </h3>
                  <p className="text-xs text-amber-200/70 mt-0.5">
                    Your release NFT was minted but your submission has not made
                    it to our queue
                  </p>
                </div>
              </div>
              <Button
                onClick={handleResubmit}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                variant="outline"
                size="sm"
                className="bg-amber-900/30 border-amber-700 hover:bg-amber-900/50 text-amber-300 flex-shrink-0"
                disabled={isSubmitting || isSuccess}
              >
                <AnimatePresence mode="wait">
                  {isSuccess ? (
                    <motion.div
                      key="success"
                      className="flex items-center gap-2"
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.8, opacity: 0 }}
                      transition={{
                        type: 'spring',
                        stiffness: 500,
                        damping: 20,
                      }}
                    >
                      <CheckCircleIcon className="size-5 fill-green-500" />
                      <span className="text-green-500 font-medium">
                        Submitted
                      </span>
                    </motion.div>
                  ) : isSubmitting ? (
                    <motion.div
                      key="submitting"
                      className="flex items-center gap-2"
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0.8, opacity: 0 }}
                      transition={{
                        type: 'spring',
                        stiffness: 500,
                        damping: 20,
                      }}
                    >
                      <div className="flex items-center gap-1">
                        {[0, 1, 2].map((i) => (
                          <motion.span
                            key={i}
                            className="size-1.5 bg-amber-300 rounded-full"
                            animate={{
                              scale: [1, 1.3, 1],
                              opacity: [0.5, 1, 0.5],
                            }}
                            transition={{
                              duration: 0.8,
                              repeat: Number.POSITIVE_INFINITY,
                              delay: i * 0.15,
                              ease: 'easeInOut',
                            }}
                          />
                        ))}
                      </div>
                      <span>Submitting…</span>
                    </motion.div>
                  ) : (
                    <motion.div
                      key="resubmit"
                      className="flex items-center"
                      exit={{ scale: 0.8, opacity: 0 }}
                      transition={{
                        type: 'spring',
                        stiffness: 500,
                        damping: 20,
                      }}
                    >
                      <motion.div
                        animate={{
                          x: isHovered ? 4 : 0,
                          rotate: isHovered ? -15 : 0,
                        }}
                        transition={{
                          type: 'spring',
                          stiffness: 400,
                          damping: 17,
                        }}
                      >
                        <SendIcon className="size-4 mr-2 fill-white" />
                      </motion.div>
                      Resubmit
                    </motion.div>
                  )}
                </AnimatePresence>
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
